import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import { requestPasswordReset, formatPasswordResetError } from '../utils/passwordReset';

const LOGIN_GOLD = '#d4af37';

export default function ForgotPasswordScreen({ navigation }) {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      alert('Enter your email address');
      return;
    }

    setBusy(true);
    try {
      await requestPasswordReset(trimmed);
      setSent(true);
    } catch (error) {
      console.error('Password reset request error:', error);
      alert(formatPasswordResetError(error));
    } finally {
      setBusy(false);
    }
  };

  const styles = getStyles(theme);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      data-screen="forgot-password"
    >
      <StatusBar style="light" />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          <Text style={styles.title}>Reset password</Text>
          <Text style={styles.subtitle}>
            Enter the email on your account. If we find it, we will send a secure reset link.
          </Text>

          {sent ? (
            <View style={styles.successBox}>
              <Text style={styles.successText}>
                If an account exists for that email, a reset link has been sent. Check your inbox and
                spam folder, then open the link to choose a new password.
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.inputContainer}>
                <Text style={styles.label}>Email</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter your email"
                  placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!busy}
                />
              </View>

              <TouchableOpacity
                style={[styles.primaryButton, busy && styles.primaryButtonDisabled]}
                onPress={() => void handleSubmit()}
                disabled={busy}
                activeOpacity={0.85}
              >
                <Text style={styles.primaryButtonText}>
                  {busy ? 'Sending…' : 'Send reset link'}
                </Text>
              </TouchableOpacity>
            </>
          )}

          <TouchableOpacity
            style={styles.backLink}
            onPress={() => navigation.navigate('Login')}
            disabled={busy}
          >
            <Text style={styles.backLinkText}>← Back to login</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const getStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#000000',
    },
    scrollContent: {
      flexGrow: 1,
      justifyContent: 'center',
      paddingHorizontal: 30,
      paddingVertical: 40,
    },
    content: {
      width: '100%',
      maxWidth: 420,
      alignSelf: 'center',
    },
    title: {
      fontSize: 28,
      fontWeight: 'bold',
      color: LOGIN_GOLD,
      textAlign: 'center',
      marginBottom: 12,
    },
    subtitle: {
      fontSize: 15,
      lineHeight: 22,
      color: '#CCCCCC',
      textAlign: 'center',
      marginBottom: 28,
    },
    inputContainer: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      color: LOGIN_GOLD,
      marginBottom: 8,
      fontWeight: '600',
    },
    input: {
      backgroundColor: '#2a2a2a',
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 14,
      fontSize: 16,
      color: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#3a3a3a',
    },
    primaryButton: {
      backgroundColor: LOGIN_GOLD,
      borderRadius: 8,
      paddingVertical: 16,
      alignItems: 'center',
      marginTop: 4,
    },
    primaryButtonDisabled: {
      opacity: 0.85,
    },
    primaryButtonText: {
      color: '#000000',
      fontSize: 17,
      fontWeight: 'bold',
    },
    successBox: {
      backgroundColor: '#1a1a1a',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#3a3a3a',
      padding: 16,
      marginBottom: 8,
    },
    successText: {
      color: '#E8E8E8',
      fontSize: 14,
      lineHeight: 21,
      textAlign: 'center',
    },
    backLink: {
      marginTop: 24,
      alignItems: 'center',
    },
    backLinkText: {
      color: LOGIN_GOLD,
      fontSize: 14,
    },
  });
